import { useState } from 'react';
import { Badge, Button, Dialog, Field, Input, Select } from '@/components/ui';
import { useToast } from '@/components/ui';
import type { StaffRow } from '@/features/admin-v2/api';
import {
  useAddStaffRole,
  useRemoveStaffRole,
  useSendStaffReset,
  useStaffAccess,
  type StaffResetSent,
} from '@/features/admin-v2/staff-access-api';
import { useMe } from '@/features/auth/session-context';
import { isApiError } from '@/lib/api';
import parity from '@/pages/ops/parity.module.css';
import { ROLES, endOfDayMyt } from './staff-roles';

const message = (e: unknown) =>
  isApiError(e) ? (e.problem?.detail ?? e.message) : 'Something went wrong.';

/** One reason, then the action (role removal, end or restore access). */
function ReasonDialog({
  title,
  body,
  confirm,
  danger,
  extra,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirm: string;
  danger?: boolean;
  extra?: React.ReactNode;
  busy: boolean;
  error: unknown;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const ok = reason.trim().length >= 3;
  return (
    <Dialog
      open
      variant="modal"
      title={title}
      onClose={onClose}
      dismissible={!busy}
      footer={
        <>
          <Button disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            disabled={!ok}
            loading={busy}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirm}
          </Button>
        </>
      }
    >
      <div className={parity.form}>
        <p>{body}</p>
        {extra}
        <Field label="Reason" hint="Kept in the audit trail.">
          <Input
            value={reason}
            maxLength={300}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        {error ? <p role="alert">{message(error)}</p> : null}
      </div>
    </Dialog>
  );
}

/** RL-1783: roles and branches on the staff record (Super admin). */
export function StaffRolesSection({ staff }: { staff: StaffRow }) {
  const me = useMe();
  const toast = useToast();
  const add = useAddStaffRole(staff.id);
  const remove = useRemoveStaffRole(staff.id);
  const [role, setRole] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [removing, setRemoving] = useState<{
    assignmentId: string;
    label: string;
  } | null>(null);
  const def = ROLES.find((r) => r.value === role);
  const canAdd =
    !!def && reason.trim().length >= 3 && (def.allBranches || codes.length > 0);
  const roles = staff.roles ?? [];

  async function addRole() {
    try {
      await add.mutateAsync({
        role,
        branchCodes: def?.allBranches ? [] : codes,
        reason: reason.trim(),
      });
      toast.show({ tone: 'success', title: `${def?.label} added` });
      setRole('');
      setCodes([]);
      setReason('');
    } catch {
      // Shown inline below.
    }
  }

  return (
    <section aria-label="Roles and branches">
      <strong>Roles and branches</strong>
      <ul className={parity.form}>
        {roles.map((r) => {
          const label = `${r.roleName ?? r.role} · ${r.branchName ?? r.branch ?? 'All branches'}`;
          return (
            <li
              key={r.assignmentId ?? `${r.role}-${r.branch}`}
              className={parity.split}
            >
              <span>{label}</span>
              {r.assignmentId && r.role !== 'super_admin' ? (
                <Button
                  size="sm"
                  aria-label={`Remove ${label}`}
                  onClick={() =>
                    setRemoving({ assignmentId: r.assignmentId!, label })
                  }
                >
                  Remove role
                </Button>
              ) : (
                <Badge tone="neutral">System team</Badge>
              )}
            </li>
          );
        })}
      </ul>
      <div className={parity.form}>
        <Field label="Add role">
          <Select
            value={role}
            placeholder="Choose a role"
            options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
            onChange={(e) => {
              setRole(e.target.value);
              setCodes([]);
            }}
          />
        </Field>
        {def && !def.allBranches ? (
          <fieldset>
            <legend>Branches</legend>
            {me.branches.map((b) => (
              <label key={b.id}>
                <input
                  type="checkbox"
                  checked={codes.includes(b.code)}
                  onChange={(e) =>
                    setCodes((c) =>
                      e.target.checked
                        ? [...c, b.code]
                        : c.filter((x) => x !== b.code),
                    )
                  }
                />{' '}
                {b.name}
              </label>
            ))}
          </fieldset>
        ) : def ? (
          <p>{def.label} covers every branch.</p>
        ) : null}
        {def ? (
          <Field label="Reason" hint="Kept in the audit trail.">
            <Input
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        ) : null}
        {add.error ? <p role="alert">{message(add.error)}</p> : null}
        <Button
          size="sm"
          disabled={!canAdd}
          loading={add.isPending}
          onClick={() => void addRole()}
        >
          Add role
        </Button>
      </div>
      {removing && (
        <ReasonDialog
          title={`Remove ${removing.label}?`}
          body={`${staff.fullName} loses this role on their next page. If it is the role they are using, they choose a role again. Kept on record.`}
          confirm="Remove role"
          danger
          busy={remove.isPending}
          error={remove.error}
          onClose={() => setRemoving(null)}
          onConfirm={(r) =>
            void remove
              .mutateAsync({ assignmentId: removing.assignmentId, reason: r })
              .then(() => {
                toast.show({ tone: 'success', title: 'Role removed' });
                setRemoving(null);
              })
              .catch(() => undefined)
          }
        />
      )}
    </section>
  );
}

/** RL-1786: End access / Restore access on the staff record. */
export function StaffAccessSection({ staff }: { staff: StaffRow }) {
  const me = useMe();
  const toast = useToast();
  const access = useStaffAccess(staff.id);
  const [open, setOpen] = useState<'end' | 'restore' | null>(null);
  const [lastDay, setLastDay] = useState('');
  const [now] = useState(() => Date.now());
  const ends = staff.accessEndsAt ? new Date(staff.accessEndsAt) : null;
  const ended =
    staff.status !== 'active' || (ends !== null && ends.getTime() <= now);
  const self = me.id === staff.id;
  const state = ended
    ? 'Access ended'
    : ends
      ? `Access ends ${ends.toLocaleString('en-GB', { timeZone: 'Asia/Kuala_Lumpur' })}`
      : 'Can sign in';

  return (
    <section aria-label="Access">
      <div className={parity.split}>
        <span>
          <strong>Access</strong> · {state}
        </span>
        {self ? null : ended || ends ? (
          <Button size="sm" onClick={() => setOpen('restore')}>
            Restore access
          </Button>
        ) : null}
        {!self && !ended ? (
          <Button size="sm" variant="danger" onClick={() => setOpen('end')}>
            End access
          </Button>
        ) : null}
      </div>
      {open === 'end' && (
        <ReasonDialog
          title={`End access for ${staff.fullName}?`}
          body="Now: they are signed out everywhere at once. On a last day: they can sign in until midnight after it. Their records, roles and history stay."
          confirm="End access"
          danger
          busy={access.isPending}
          error={access.error}
          extra={
            <Field label="Last day (optional)" hint="Leave empty to end now.">
              <Input
                type="date"
                value={lastDay}
                onChange={(e) => setLastDay(e.target.value)}
              />
            </Field>
          }
          onClose={() => setOpen(null)}
          onConfirm={(reason) =>
            void access
              .mutateAsync({
                action: 'end',
                reason,
                at: lastDay ? endOfDayMyt(lastDay) : undefined,
              })
              .then((r) => {
                toast.show({
                  tone: 'success',
                  title:
                    r.access === 'ended'
                      ? `Access ended; ${r.sessionsRevoked} session(s) signed out`
                      : 'Access will end after the last day',
                });
                setOpen(null);
              })
              .catch(() => undefined)
          }
        />
      )}
      {open === 'restore' && (
        <ReasonDialog
          title={`Restore access for ${staff.fullName}?`}
          body="They can sign in again with the same roles."
          confirm="Restore access"
          busy={access.isPending}
          error={access.error}
          onClose={() => setOpen(null)}
          onConfirm={(reason) =>
            void access
              .mutateAsync({ action: 'restore', reason })
              .then(() => {
                toast.show({ tone: 'success', title: 'Access restored' });
                setOpen(null);
              })
              .catch(() => undefined)
          }
        />
      )}
    </section>
  );
}

/** RL-1788: Send password reset on the staff record. */
export function StaffPasswordSection({ staff }: { staff: StaffRow }) {
  const send = useSendStaffReset(staff.id);
  const [result, setResult] = useState<StaffResetSent | null>(null);
  return (
    <section aria-label="Password">
      <div className={parity.split}>
        <span>
          Email {staff.fullName} a link to choose a new password (one hour). You
          never see the password; their two-factor code is still needed.
        </span>
        <Button
          size="sm"
          loading={send.isPending}
          onClick={() =>
            void send
              .mutateAsync()
              .then(setResult)
              .catch(() => undefined)
          }
        >
          Send password reset
        </Button>
      </div>
      {send.error ? <p role="alert">{message(send.error)}</p> : null}
      {result?.delivery === 'emailed' ? (
        <p role="status">Reset link emailed to {staff.email}.</p>
      ) : result ? (
        <div role="status" className={parity.form}>
          <p>
            No email could be sent. Give this link to {staff.fullName} in
            person; it works once, for {result.expiresInMinutes} minutes:
          </p>
          <Input readOnly value={result.link ?? ''} aria-label="Reset link" />
        </div>
      ) : null}
    </section>
  );
}
